import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Calendar,
    Plus,
    Edit3,
    Trash2,
    Eye,
    Clock,
    MapPin,
    Users,
    Star,
    Filter,
    Search,
    MoreVertical,
    CheckCircle,
    AlertCircle,
    XCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const EventManagement = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Use admin endpoint to get all events
            const response = await api.get('/events?limit=100&order_by=created_at&order_dir=DESC');
            
            if (response.data.success) {
                setEvents(response.data.data);
            } else {
                setError('Failed to load events');
            }
        } catch (err) {
            console.error('Error fetching events:', err);
            setError('Unable to load events. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            // Use hard delete for admin interface
            await api.delete(`/events/${eventId}?hard_delete=true`);
            setEvents(events.filter(event => event.id !== eventId));
            setShowDeleteModal(false);
            setEventToDelete(null);
        } catch (err) {
            console.error('Error deleting event:', err);
            alert('Failed to delete event. Please try again.');
        }
    };

    const handleBulkDelete = async () => {
        try {
            // Use hard delete for admin bulk operations
            await Promise.all(selectedEvents.map(id => api.delete(`/events/${id}?hard_delete=true`)));
            setEvents(events.filter(event => !selectedEvents.includes(event.id)));
            setSelectedEvents([]);
        } catch (err) {
            console.error('Error bulk deleting events:', err);
            alert('Failed to delete some events. Please try again.');
        }
    };

    const toggleEventSelection = (eventId) => {
        setSelectedEvents(prev => 
            prev.includes(eventId) 
                ? prev.filter(id => id !== eventId)
                : [...prev, eventId]
        );
    };

    const selectAllEvents = () => {
        if (selectedEvents.length === filteredEvents.length) {
            setSelectedEvents([]);
        } else {
            setSelectedEvents(filteredEvents.map(event => event.id));
        }
    };

    // Filter events
    const filteredEvents = events.filter(event => {
        const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            event.venue_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            event.city?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
        const matchesType = typeFilter === 'all' || event.event_type === typeFilter;
        
        return matchesSearch && matchesStatus && matchesType;
    });

    const getStatusBadge = (status) => {
        const statusConfig = {
            published: { color: 'bg-green-600/20 text-green-400', icon: CheckCircle },
            draft: { color: 'bg-yellow-600/20 text-yellow-400', icon: Clock },
            cancelled: { color: 'bg-red-600/20 text-red-400', icon: XCircle },
            postponed: { color: 'bg-orange-600/20 text-orange-400', icon: AlertCircle },
            completed: { color: 'bg-gray-600/20 text-gray-400', icon: CheckCircle }
        };
        
        const config = statusConfig[status] || statusConfig.draft;
        const Icon = config.icon;
        
        return (
            <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${config.color}`}>
                <Icon className="w-3 h-3" />
                {status}
            </span>
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading events...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <Calendar className="w-8 h-8 text-blue-400" />
                        <h1 className="text-4xl font-black gradient-text">Ensemble Events</h1>
                    </div>
                    
                    <Link to="/admin/events/create" className="btn-primary flex items-center gap-2">
                        <Plus className="w-5 h-5" />
                        Create Showcase
                    </Link>
                </div>

                {/* Filters and Search */}
                <div className="glass-card p-6 mb-8">
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                        {/* Search */}
                        <div className="flex-1 min-w-[300px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search ensemble events..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-600"
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-600"
                        >
                            <option value="all">All Status</option>
                            <option value="published">Published</option>
                            <option value="draft">Draft</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="postponed">Postponed</option>
                            <option value="completed">Completed</option>
                        </select>

                        {/* Type Filter */}
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-blue-600"
                        >
                            <option value="all">All Types</option>
                            <option value="concert">Concerts</option>
                            <option value="festival">Festivals</option>
                            <option value="livestream">Live Streams</option>
                            <option value="workshop">Workshops</option>
                            <option value="meetup">Meetups</option>
                            <option value="release">Releases</option>
                        </select>
                    </div>

                    {/* Bulk Actions */}
                    {selectedEvents.length > 0 && (
                        <div className="flex items-center gap-4 p-3 bg-blue-600/20 rounded-lg border border-blue-600/30">
                            <span className="text-blue-300">
                                {selectedEvents.length} event(s) selected
                            </span>
                            <button
                                onClick={handleBulkDelete}
                                className="flex items-center gap-2 px-3 py-1 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                                Delete Selected
                            </button>
                            <button
                                onClick={() => setSelectedEvents([])}
                                className="text-gray-400 hover:text-white"
                            >
                                Clear Selection
                            </button>
                        </div>
                    )}
                </div>

                {error && (
                    <div className="glass-card p-6 mb-8 border border-red-500/30">
                        <p className="text-red-400">{error}</p>
                        <button 
                            onClick={fetchEvents}
                            className="btn-secondary mt-4"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {/* Events Table */}
                <div className="glass-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-800/50">
                                <tr>
                                    <th className="text-left p-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedEvents.length === filteredEvents.length && filteredEvents.length > 0}
                                            onChange={selectAllEvents}
                                            className="rounded border-gray-600 text-blue-600 focus:ring-blue-600"
                                        />
                                    </th>
                                    <th className="text-left p-4 text-gray-300 font-semibold">Event</th>
                                    <th className="text-left p-4 text-gray-300 font-semibold">Date & Time</th>
                                    <th className="text-left p-4 text-gray-300 font-semibold">Location</th>
                                    <th className="text-left p-4 text-gray-300 font-semibold">Status</th>
                                    <th className="text-left p-4 text-gray-300 font-semibold">Type</th>
                                    <th className="text-left p-4 text-gray-300 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEvents.map((event) => (
                                    <tr key={event.id} className="border-t border-gray-700/50 hover:bg-gray-800/30">
                                        <td className="p-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedEvents.includes(event.id)}
                                                onChange={() => toggleEventSelection(event.id)}
                                                className="rounded border-gray-600 text-blue-600 focus:ring-blue-600"
                                            />
                                        </td>
                                        
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                {!!event.is_featured && (
                                                    <Star className="w-5 h-5 text-yellow-400" />
                                                )}
                                                <div>
                                                    <h3 className="text-white font-semibold">{event.title}</h3>
                                                    <p className="text-gray-400 text-sm">
                                                        by {event.creator_name || event.creator_email}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>
                                        
                                        <td className="p-4">
                                            <div className="text-white">
                                                {formatDate(event.start_date)}
                                            </div>
                                            {event.end_date && (
                                                <div className="text-gray-400 text-sm">
                                                    to {formatDate(event.end_date)}
                                                </div>
                                            )}
                                        </td>
                                        
                                        <td className="p-4">
                                            <div className="text-white">
                                                {event.venue_name || 'No venue'}
                                            </div>
                                            {event.city && (
                                                <div className="text-gray-400 text-sm flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    {event.city}
                                                    {event.state && `, ${event.state}`}
                                                </div>
                                            )}
                                        </td>
                                        
                                        <td className="p-4">
                                            {getStatusBadge(event.status)}
                                        </td>
                                        
                                        <td className="p-4">
                                            <span className="capitalize text-gray-300">
                                                {event.event_type}
                                            </span>
                                        </td>
                                        
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <Link
                                                    to={`/events/${event.slug || event.id}`}
                                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                                    title="View Event"
                                                >
                                                    <Eye className="w-4 h-4 text-gray-400 hover:text-blue-400" />
                                                </Link>
                                                
                                                <Link
                                                    to={`/admin/events/${event.id}/edit`}
                                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                                    title="Edit Event"
                                                >
                                                    <Edit3 className="w-4 h-4 text-gray-400 hover:text-blue-400" />
                                                </Link>
                                                
                                                <button
                                                    onClick={() => {
                                                        setEventToDelete(event);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                                                    title="Delete Event"
                                                >
                                                    <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-400" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {filteredEvents.length === 0 && !loading && (
                        <div className="p-8 text-center">
                            <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-gray-300 mb-2">No events found</h3>
                            <p className="text-gray-400 mb-4">
                                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
                                    ? 'No events match your current filters.'
                                    : 'No events have been created yet.'
                                }
                            </p>
                            {(!searchTerm && statusFilter === 'all' && typeFilter === 'all') && (
                                <Link to="/admin/events/create" className="btn-primary flex items-center gap-2">
                                    <Plus className="w-5 h-5" />
                                    Create Your First Event
                                </Link>
                            )}
                        </div>
                    )}
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && eventToDelete && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                        <div className="glass-card max-w-md w-full p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-3 bg-red-600/20 rounded-lg">
                                    <Trash2 className="w-6 h-6 text-red-400" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-white">Delete Event</h3>
                                    <p className="text-gray-400">This action cannot be undone</p>
                                </div>
                            </div>
                            
                            <p className="text-gray-300 mb-6">
                                Are you sure you want to delete "<strong>{eventToDelete.title}</strong>"?
                                This will permanently remove the event and all associated data.
                            </p>
                            
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setEventToDelete(null);
                                    }}
                                    className="flex-1 btn-ghost"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleDeleteEvent(eventToDelete.id)}
                                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                                >
                                    Delete Event
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EventManagement;